import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import SystemDataService from '../../Services/SystemDataService';
import { getFaqData } from '../../Public/faq-data.js';

const AccordionItem = ({ item, isActive, onToggle }) => {
    const ref = useRef(null);
    return (
        <div
            className={`rounded-2xl border bg-white px-5 md:px-6 transition-all duration-200 ${
                isActive
                    ? 'border-secondary-500 shadow-[0_8px_30px_rgba(124,58,237,0.10)]'
                    : 'border-gray-200 hover:border-secondary-300'
            }`}
        >
            <button
                onClick={() => onToggle(item.id)}
                aria-expanded={isActive}
                className="w-full flex items-start justify-between py-5 gap-4 cursor-pointer text-start"
            >
                <span className={`text-base font-bold text-balance leading-snug transition-colors ${isActive ? 'text-secondary-600' : 'text-primary-900'}`}>
                    {item.question}
                </span>
                <span className={`grid place-items-center w-7 h-7 shrink-0 rounded-full transition-all duration-200 ${isActive ? 'bg-secondary-500 text-white rotate-180' : 'bg-secondary-50 text-secondary-500'}`}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                    </svg>
                </span>
            </button>
            <div
                ref={ref}
                style={{ maxHeight: isActive ? `${ref.current?.scrollHeight ?? 200}px` : '0px' }}
                className="overflow-hidden transition-all max-w-4xl duration-300"
            >
                <p className="pb-5 text-sm text-primary-900/60 font-normal text-pretty text-start leading-relaxed">
                    {item.answer}
                </p>
            </div>
        </div>
    );
};

const FaqSection = ({ faqCards: parentFaqCards }) => {
    const { language, dir } = useSelector(s => s.systemData || {});
    const txt = useMemo(() => getFaqData(language), [language]);

    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeId, setActiveId] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const init = async () => {
            setLoading(true);
            const sanitize = (raw) => raw.map((c, i) => ({ ...c, id: c.id || `faq-${i}` }));
            if (parentFaqCards?.length > 0) {
                if (isMounted) { setCards(sanitize(parentFaqCards)); setLoading(false); }
                return;
            }
            try {
                const apiData = await SystemDataService.getCardFaq();
                if (apiData?.length > 0 && isMounted) {
                    setCards(sanitize(apiData));
                    setLoading(false);
                    return;
                }
            } catch (e) { console.error('FAQ Load Error:', e); }
            if (isMounted) { setCards(sanitize(txt.items || [])); setLoading(false); }
        };
        init();
        return () => { isMounted = false; };
    }, [parentFaqCards, txt.items]);

    if (!loading && cards.length === 0) return null;

    return (
        <section dir={dir} className="w-full bg-gray-50 border-y border-gray-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">

                {/* Header, big headline like the contact page, no eyebrow badge */}
                <div className={`mb-8 md:mb-12 ${dir === 'ltr' ? 'text-left' : 'text-right'}`}>
                    <h2
                        className="font-secondary font-bold text-5xl text-center sm:text-left leading-[0.82] text-secondary-500"
                    >
                        {language === 'he' ? 'שאלות?' : 'Questions?'}
                    </h2>
                    {txt.subtitle && (
                        <p className="mt-6 max-w-2xl text-lg md:text-xl text-primary-900/50 font-primary leading-relaxed">
                            {txt.subtitle}
                        </p>
                    )}
                </div>

                {/* Single accordion, all screen sizes, no duplicate DOM nodes */}
                {loading ? (
                    <div className="flex flex-col gap-4">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="h-24 bg-gray-200 rounded animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {cards.map(item => (
                            <AccordionItem
                                key={item.id}
                                item={item}
                                isActive={activeId === item.id}
                                onToggle={(id) => setActiveId(activeId === id ? null : id)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default FaqSection;
